// Fitting a trace into its lane, as a monitor does. The lowest and highest points a trace reaches are read from the
// synthesiser itself (the same beats, waves and wander the sweep draws), so a lane never clips a complex or runs it under
// its label. A calibrated strip keeps its gain and only places its baseline; the bedside monitor sizes its ECG to the lane
// (a monitor's auto-size), since its trace carries no calibration.
import type { EcgParams, Lead } from '../ecg/types';
import { sampleLead } from '../ecg/synth';

export interface Extent { min: number; max: number }

/** The lowest and highest millivolts `lead` reaches over `seconds` of trace (sampled every 4 ms), noise and wander included. */
export function traceExtent(params: EcgParams, lead: Lead = 'II', seconds = 12): Extent {
  let min = Infinity, max = -Infinity;
  for (let t = 0; t <= seconds; t += 0.004) {
    const y = sampleLead(params, t, lead);
    if (y < min) min = y;
    if (y > max) max = y;
  }
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : { min: 0, max: 0 };
}

/**
 * The baseline (px from the lane's top) for a trace drawn at `pxPerMv` in a lane whose usable part runs from `top` to `bottom`:
 * `preferred` when the whole extent fits there, else the nearest baseline that keeps it inside, else (taller than the lane) the
 * one that clips both ends evenly.
 */
export function placeBaseline(extent: Extent, pxPerMv: number, top: number, bottom: number, preferred: number): number {
  const lo = top + Math.max(0, extent.max) * pxPerMv;     // highest point no higher than `top`
  const hi = bottom + Math.min(0, extent.min) * pxPerMv;  // deepest point no lower than `bottom`
  if (lo > hi) return (lo + hi) / 2;
  return Math.min(hi, Math.max(lo, preferred));
}

/**
 * A monitor's auto-size: the gain (px per mV) that makes the trace's whole extent fill `fill` of the lane from `top` to
 * `bottom`, centred in it; a flat trace is never blown up past the gain at which `minSpanMv` would fill it.
 */
export function autoSize(extent: Extent, top: number, bottom: number, fill = 0.9, minSpanMv = 1): { gain: number; base: number } {
  const room = Math.max(1, bottom - top);
  const span = Math.max(minSpanMv, extent.max - extent.min);
  const gain = (fill * room) / span;
  const drawn = (extent.max - extent.min) * gain;
  return { gain, base: top + (room - drawn) / 2 + extent.max * gain };
}
